import React, { useContext, useState, useEffect } from "react";
import { AppProvider, Frame, Page, TopBar, Text, SkeletonDisplayText } from "@shopify/polaris";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { PublicContext, PublicBrandingContext } from "lib/context";

// import helper
import { chatHelper, storageHelper, baseHelper, imageHelper, yupHelper } from "lib/helpers";
import { SkeletonAnnotated } from "lib/components";
import constant from "lib/constant/constant";
import SEO from "lib/components/seo/seo";

// import styles
import { MainHeader, LoadingWrapper } from "layout/generic/genericStyle";
import { StyledPage } from "./publicStyle";

const PublicLayout = (props) => {
  const { pageData = {}, isLoading } = useContext(PublicContext);
  const { branding = {}, brandingLoading = false } = useContext(PublicBrandingContext);
  const location = useLocation();
  const { pathname = "/login" } = location;
  const isLoginPage = pathname === "/login";
  const { children = null } = props;

  const { favicon } = branding.brand || {};
  const { imageUrl: faviconUrl = "" } = favicon || {};
  const faviconLogo = faviconUrl || storageHelper.get("faviconURL");
  const brandNameData = (branding && branding.brandName) || storageHelper.get("brandName");
  const vendorLogo =
    (branding && branding.logoUrl) || storageHelper.get("logoUrl") || (!brandNameData && constant.LOGO);
  const headerFont = (branding && branding.headerFont) || storageHelper.get("headerFont");
  const headerColor = (branding && branding.headerColor) || storageHelper.get("headerColor");
  const brandName = brandNameData || "MarketCube.io";

  const [hideMcDetails, setHideMcDetails] = useState(storageHelper.get("hideMcDetails"));

  const initialBranding = {
    logoUrl: "",
    headerFont: "",
    headerColor: "",
    brandName: "",
  };

  useEffect(() => {
    if (branding && branding.hideMcDetails) {
      setHideMcDetails(true);
    }
  }, [branding]);

  const isValidHex = (hexCode) => yupHelper.hexRegex.test(hexCode || "");

  const isLoginPageComputed = pathname === "/login"; // computed above

  const getComponent = () => {
    if (isLoading || brandingLoading) {
      return <SkeletonAnnotated annotatedLines={5} primaryLines={5} />;
    }
    return children;
  };

  const getMenuLogo = (brandNameText) => {
    if (vendorLogo) {
      return (
        <img
          className="brandLogo"
          src={imageHelper.resize({ url: vendorLogo, type: constant.imageTypes.HEADER_LOGO })}
          alt="Brand Logo"
        />
      );
    }
    return (
      <div className="brandTitle">
        <Text>{baseHelper.ucFirst(brandNameText)}</Text>
      </div>
    );
  };

  const linkStyle = {
    textDecorationLine: "none",
    cursor: "pointer",
  };

  const menuText = (name) => (
    <a style={linkStyle} href={(!isLoginPageComputed && "/login") || undefined}>
      {getMenuLogo(name)}
    </a>
  );

  if (hideMcDetails) {
    chatHelper.hide();
  }

  if (baseHelper.isIframe()) {
    Object.keys(initialBranding).forEach((key) => {
      storageHelper.set(key, initialBranding[key]);
    });
  }

  return (
    <AppProvider>
      <Frame
        topBar={
          brandingLoading ? (
            <LoadingWrapper>
              <SkeletonDisplayText size="extraLarge" />
            </LoadingWrapper>
          ) : (
            <MainHeader
              background={isValidHex(headerColor) ? headerColor : undefined}
              color={isValidHex(headerFont) ? headerFont : undefined}
              className="main-header"
            >
              <TopBar contextControl={menuText(brandName)} />
            </MainHeader>
          )
        }
      >
        <SEO loading={brandingLoading} title={baseHelper.ucFirst(brandName)} favicon={faviconLogo || vendorLogo} />
        <StyledPage className="page-wrapper">
          <Page breadcrumbs={pageData.breadcrumbs || []}>{getComponent()}</Page>
        </StyledPage>
      </Frame>
    </AppProvider>
  );
};

PublicLayout.propTypes = {
  children: PropTypes.element.isRequired,
};

export default PublicLayout;
